import { CreateGuildWizard } from '../features/create-guild/CreateGuildWizard.js';
import { useGuild } from '../features/create-guild/use-guild.js';
import { GameShell } from '../features/shell/GameShell.js';
import { ErrorState, Skeleton } from '../shared/ui/controls.js';
import { ClosedAccessScreen } from './closed-access-screen.js';
import { useAuth } from './use-auth.js';
import { useBalance } from './use-balance.js';

/*
 * Корень приложения.
 *
 * Четыре состояния на каждом шаге: загрузка — скелет, пусто, ошибка
 * с кнопкой «Повторить», готово. Автоповтора POST нет: повтор решает
 * игрок, иначе сетевой сбой мог бы создать вторую гильдию.
 */
export function App(): React.ReactElement {
  const { state: auth, retry: retryAuth, unlock } = useAuth();
  const authed = auth.status === 'ready';

  const balance = useBalance(authed);
  const guild = useGuild(authed);
  const accessRevoked =
    guild.state.status === 'error' && guild.state.code === 'CLOSED_ACCESS_REQUIRED';

  if (auth.status === 'locked') {
    return <ClosedAccessScreen busy={auth.busy} message={auth.message} onUnlock={unlock} />;
  }

  if (auth.status === 'loading') {
    // Скелет разметки, не крутилка: игрок видит форму будущего экрана.
    return (
      <main className="screen">
        <Skeleton variant="card" />
        <Skeleton variant="title" />
        <Skeleton variant="text" />
      </main>
    );
  }

  if (auth.status === 'empty') {
    return (
      <main className="screen screen--center enter">
        <section className="state-panel">
          <h1 className="h1">GuildRise</h1>
          <p className="body">Игра запускается из Telegram.</p>
          <p className="hint">Открой бота и нажми кнопку запуска.</p>
        </section>
      </main>
    );
  }

  if (auth.status === 'error') {
    return <ErrorState title="Не удалось войти" message={auth.message} onRetry={retryAuth} />;
  }

  if (guild.state.status === 'error') {
    return (
      <ErrorState
        title={accessRevoked ? 'Доступ изменился' : 'Сервер не ответил'}
        message={guild.state.message}
        onRetry={accessRevoked ? retryAuth : guild.retry}
      />
    );
  }

  // Баланс нужен обоим экранам: в нём каталог рамок и карт.
  if (guild.state.status === 'loading' || balance.status === 'loading') {
    return (
      <main className="screen">
        <Skeleton variant="card" />
        <Skeleton variant="title" />
        <Skeleton variant="text" />
      </main>
    );
  }

  if (balance.status === 'error') {
    return <ErrorState title="Данные игры не загрузились" message={balance.message} />;
  }

  if (guild.state.status === 'ready') {
    const frameId = guild.state.guild.frameId;
    const frame = balance.bundle.frames.find((f) => f.frame_id === frameId);
    return (
      <GameShell
        guild={guild.state.guild}
        player={auth.user}
        frameArt={frame?.kind === 'art' ? frame.art : undefined}
        balance={balance.bundle}
      />
    );
  }

  return (
    <CreateGuildWizard
      balance={balance.bundle}
      playerName={auth.user.firstName}
      busy={guild.state.busy}
      errorMessage={guild.state.errorMessage}
      takenSuggestion={guild.state.takenSuggestion}
      onSubmit={(input) => void guild.submit(input)}
      onUploadLogo={guild.upload}
    />
  );
}
